import type React from "react";
import { useEffect, useState } from "react";
import Button from "~/components/ui/button";
import close from "~/images/close.svg";
import styles from "./index.module.scss";
import {
  CheckBoxOptions,
  DateOptions,
  DropDownOptions,
  FileOptions,
  GroupOptions,
  GroupSelectOptions,
  LinkOptions,
  LookupOptions,
  MultiLineTextOptions,
  MultiSelectOptions,
  NumberOptions,
  RadioButtonOptions,
  ReferenceTableOptions,
  RichTextOptions,
  SingleLineTextOptions,
  SubTableOptions,
} from "./parts";

// 共通型定義をインポート
import type {
  AppData as AppDataType,
  FieldData,
  FieldOptions,
  GetFieldsForAppFn,
  OptionChangeHandler,
  OptionItem,
} from "./types";

interface ItemSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  fieldData: FieldData;
  onSave: (updatedOptions: FieldOptions) => void;
  isEditable?: boolean;
  fieldTableData?: FieldData[];
  appDataMap?: Record<string, string>;
}

const ItemSettingsModal: React.FC<ItemSettingsModalProps> = ({
  isOpen,
  onClose,
  fieldData,
  onSave,
  isEditable = false,
  fieldTableData = [], // デフォルト値として空配列を設定
  appDataMap = {}, // デフォルト値として空オブジェクトを設定
}) => {
  const [options, setOptions] = useState<FieldOptions>({});
  // 選択肢のリストを管理するための状態
  const [optionItems, setOptionItems] = useState<OptionItem[]>([]);
  // 複数選択できる初期値を管理するための状態
  const [selectedDefaultOptions, setSelectedDefaultOptions] = useState<string[]>([]);

  // 共通のgetFieldsForApp関数を実装
  const getFieldsForApp: GetFieldsForAppFn = (appId: string) => {
    // fieldTableDataからappIdに一致するフィールドをフィルタリング（文字列比較）
    return fieldTableData
      .filter((field) => String(field.app_id) === String(appId))
      .map((field) => ({
        code: field.フィールドコード || "",
        label: field.フィールド名 || "",
        type: field.フィールドタイプ || "",
        options: field.options || {},
      }));
  };

  useEffect(() => {
    if (fieldData?.options) {
      setOptions({ ...fieldData.options });

      // 選択肢のリストを初期化
      if (fieldData.options.options && Array.isArray(fieldData.options.options)) {
        // 既存の選択肢がある場合はそれを使用
        setOptionItems([...fieldData.options.options]);
      } else {
        // デフォルトの選択肢を作成
        setOptionItems([
          { id: "1", value: "サンプル1" },
          { id: "2", value: "サンプル2" },
          { id: "3", value: "サンプル3" },
          { id: "4", value: "サンプル4" },
        ]);
      }

      // 選択された初期値を設定
      if (
        fieldData.options.defaultSelectedOptions &&
        Array.isArray(fieldData.options.defaultSelectedOptions)
      ) {
        setSelectedDefaultOptions([...fieldData.options.defaultSelectedOptions]);
      } else {
        setSelectedDefaultOptions([]);
      }
    } else {
      setOptions({});
      setOptionItems([
        { id: "1", value: "サンプル1" },
        { id: "2", value: "サンプル2" },
        { id: "3", value: "サンプル3" },
        { id: "4", value: "サンプル4" },
      ]);
      setSelectedDefaultOptions([]);
    }
  }, [fieldData]);

  const handleBooleanOptionChange = (key: string, value: boolean) => {
    setOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleTextOptionChange = (key: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleRadioOptionChange = (key: string, value: string) => {
    setOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // 共通の変更ハンドラ
  const handleOptionChange: OptionChangeHandler = (key, value) => {
    setOptions((prev) => ({
      ...prev,
      [key]: value,
    }));

    // 選択肢の配列が更新された場合、optionItemsステートも更新
    if (key === "options" && Array.isArray(value)) {
      setOptionItems(value);
    }

    // 選択された初期値の配列が更新された場合、selectedDefaultOptionsステートも更新
    if (key === "defaultSelectedOptions" && Array.isArray(value)) {
      setSelectedDefaultOptions(value);
    }
  };

  const handleSubmit = async () => {
    try {
      let updatedOptions = {
        ...options,
      };

      // フィールドタイプに応じて必要なデータを追加
      if (fieldData.フィールドタイプ === "SUBTABLE") {
        // サブテーブルの場合、fieldsデータを保持
        updatedOptions = {
          ...updatedOptions,
          fields: options.fields || {},
          noLabel: options.noLabel || false,
        };
      } else if (
        ["CHECK_BOX", "RADIO_BUTTON", "MULTI_SELECT", "DROP_DOWN"].includes(
          fieldData.フィールドタイプ,
        )
      ) {
        // 選択肢を持つフィールドタイプの場合
        updatedOptions = {
          ...updatedOptions,
          options: [...optionItems],
          defaultSelectedOptions: [...selectedDefaultOptions],
        };
      }

      await onSave(updatedOptions);
    } catch (error) {
      console.error("設定の保存中にエラーが発生しました", error);
    }
  };

  // サブテーブル用の特別なハンドラーを追加
  const handleSubTableOptionChange = (key: string, value: any) => {
    if (key === "fields") {
      // fieldsの更新時は、既存のoptionsを保持しつつfieldsを更新
      setOptions((prev) => ({
        ...prev,
        fields: value,
      }));
    } else if (key === "noLabel") {
      // noLabelの更新
      setOptions((prev) => ({
        ...prev,
        noLabel: value,
      }));
    } else {
      // その他の更新
      handleOptionChange(key, value);
    }
  };

  const isReadOnly = !isEditable;

  const getAppNameFromMap = (appId: string): string => {
    return appDataMap[appId] || `アプリ${appId}`; // マップから取得、なければデフォルト
  };

  const renderFieldTypeSpecificOptions = () => {
    const fieldType = fieldData.フィールドタイプ;

    // appListをここで一度生成する
    const appListForOptions = fieldTableData.reduce((apps: AppDataType[], field) => {
      if (!apps.some((app) => app.app_id === field.app_id)) {
        const appName = getAppNameFromMap(field.app_id); // マップからアプリ名を取得
        const appInfo: AppDataType = {
          app_id: field.app_id,
          app_name: appName,
        };
        apps.push(appInfo);
      }
      return apps;
    }, []);

    switch (fieldType) {
      case "SINGLE_LINE_TEXT":
        return (
          <SingleLineTextOptions
            options={options}
            handleTextOptionChange={handleTextOptionChange}
            handleBooleanOptionChange={handleBooleanOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "MULTI_LINE_TEXT":
        return (
          <MultiLineTextOptions
            options={options}
            handleTextOptionChange={handleTextOptionChange}
            handleBooleanOptionChange={handleBooleanOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "RICH_TEXT":
        return (
          <RichTextOptions
            options={options}
            handleBooleanOptionChange={handleBooleanOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "NUMBER":
        return (
          <NumberOptions options={options} onChange={handleOptionChange} isReadOnly={isReadOnly} />
        );

      case "DATE":
      case "DATETIME":
      case "TIME":
        return (
          <DateOptions
            options={options}
            handleBooleanOptionChange={handleBooleanOptionChange}
            isReadOnly={isReadOnly}
            fieldType={fieldType}
          />
        );

      case "GROUP":
        return (
          <GroupOptions
            options={options}
            handleBooleanOptionChange={handleBooleanOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "GROUP_SELECT":
      case "USER_SELECT":
      case "ORGANIZATION_SELECT":
        return (
          <GroupSelectOptions
            options={options}
            handleBooleanOptionChange={handleBooleanOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "CHECK_BOX":
        return (
          <CheckBoxOptions
            options={options}
            onChange={handleOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "RADIO_BUTTON":
        return (
          <RadioButtonOptions
            options={options}
            onChange={handleOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "LINK":
        return (
          <LinkOptions
            options={options}
            handleRadioOptionChange={handleRadioOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "FILE":
        return (
          <FileOptions options={options} onChange={handleOptionChange} isReadOnly={isReadOnly} />
        );

      case "DROP_DOWN":
        return (
          <DropDownOptions
            options={options}
            onChange={handleOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      case "REFERENCE_TABLE":
        return (
          <ReferenceTableOptions
            options={{
              ...options,
              currentAppId: fieldData.app_id, // 現在のアプリIDを渡す
            }}
            onChange={handleOptionChange}
            getFieldsForApp={getFieldsForApp}
            isReadOnly={isReadOnly}
            appList={appListForOptions} // 生成したリストを使用
          />
        );

      case "SUBTABLE":
        return (
          <SubTableOptions
            options={options}
            onChange={handleSubTableOptionChange}
            getFieldsForApp={getFieldsForApp}
            currentAppId={fieldData.app_id || ""}
            isReadOnly={isReadOnly}
          />
        );

      case "LOOKUP":
        return (
          <LookupOptions
            options={{
              ...options,
              currentAppId: fieldData.app_id, // 現在のアプリIDを渡す
            }}
            onChange={handleOptionChange}
            getFieldsForApp={getFieldsForApp}
            isReadOnly={isReadOnly}
            appList={appListForOptions} // 生成したリストを使用
          />
        );

      case "MULTI_SELECT":
        return (
          <MultiSelectOptions
            options={options}
            onChange={handleOptionChange}
            isReadOnly={isReadOnly}
          />
        );

      default:
        return <div>このフィールドタイプの詳細設定はありません。</div>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles["overlay"]}>
      <div className={styles["modal"]}>
        <div className={styles["modalHeader"]}>
          <h2 className={styles["custom-h2"]}>詳細設定</h2>
          <div className={styles["closeButton"]} onClick={onClose}>
            <img src={close} alt="" />
          </div>
        </div>
        <div className={styles["modalContent"]}>{renderFieldTypeSpecificOptions()}</div>
        <div className={styles["modalFooter"]}>
          {isEditable && (
            <>
              <Button styleType="white" label="キャンセル" onClick={onClose} />
              <Button styleType="blue" label="保存" onClick={handleSubmit} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemSettingsModal;
